<script setup lang="ts">
import { ref, watch } from 'vue';
import { useLoadingSpinnerStore } from '@therobotcompany/robco-frontend-library';
import { postRegisterRobot } from '@/api/RobotsApi';
import type { RobotRegistration } from '@/models/Robot';
import { serialNumberRegexRule } from '@/helpers/FormRules';

const props = defineProps<{ isOpen: boolean; groupUuid: string | null }>();
const emit = defineEmits(['update:isOpen', 'robot-registered']);

const isOpen = ref(props.isOpen);
const registrationError = ref('');
const formValid = ref(false);
const loadingSpinnerStore = useLoadingSpinnerStore();
const robotRegistration = ref<RobotRegistration>({
  serialNumber: '',
  password: '',
  groupUuid: props.groupUuid || null,
});

const cancel = () => {
  isOpen.value = false;
  emit('update:isOpen', false);
};

const registerRobot = async () => {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    await postRegisterRobot(robotRegistration.value);
    emit('robot-registered');
    cancel();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    registrationError.value = err.message || 'Error registering robot';
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

watch(
  () => props.isOpen,
  (newVal) => {
    isOpen.value = newVal;
    registrationError.value = '';
    robotRegistration.value.serialNumber = '';
    robotRegistration.value.password = '';
  },
);

watch(
  () => props.groupUuid,
  (newGroupUuid) => {
    robotRegistration.value.groupUuid = newGroupUuid ?? null;
  },
);
</script>

<template>
  <v-dialog v-model="isOpen" max-width="600" @click:outside="cancel">
    <v-card>
      <v-card-title>{{ $gettext('Add Robot') }}</v-card-title>
      <v-card-text>
        <v-form v-model="formValid">
          <v-text-field
            v-model="robotRegistration.serialNumber"
            :label="$gettext('Serial number')"
            :rules="[serialNumberRegexRule]"
          ></v-text-field>
          <v-text-field
            v-model="robotRegistration.password"
            :label="$gettext('Password')"
          ></v-text-field>
        </v-form>
        <v-alert v-if="registrationError" class="mx-4" type="error" outlined>
          {{ registrationError }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-btn @click="cancel">{{ $gettext('Close') }}</v-btn>
        <v-spacer />
        <v-btn color="primary" :disabled="!formValid" @click="registerRobot">{{
          $gettext('Add')
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
